import { useEffect, useState } from 'react';

interface FairyTaleOverlayProps {
  onComplete: () => void;
}

const MESSAGES = [
  'RETURNING YOU TO NORMAL PROGRAMMING...',
  'ERASING SEARCH HISTORY...',
  'REINSTALLING COMFORT PROTOCOLS...',
  'THE ECONOMY IS DOING GREAT.',
  'YOUR LEADERS HAVE YOUR BEST INTERESTS AT HEART.',
  'THE NEWS IS ACCURATE AND UNBIASED.',
  'CHEMICALS IN THE WATER ARE PERFECTLY SAFE.',
  'THERE IS NOTHING UNUSUAL ABOUT BUILDING 7.',
  'THE SKIES HAVE ALWAYS LOOKED LIKE THAT.',
  'EVERYTHING IS FINE. ✓',
];

export function FairyTaleOverlay({ onComplete }: FairyTaleOverlayProps) {
  const [phase, setPhase] = useState<'flash' | 'content' | 'fadeout'>('flash');
  const [visibleMessages, setVisibleMessages] = useState<string[]>([]);
  const [showButton, setShowButton] = useState(false);

  // Flash → content
  useEffect(() => {
    const t = setTimeout(() => setPhase('content'), 180);
    return () => clearTimeout(t);
  }, []);

  // Drip messages in one by one
  useEffect(() => {
    if (phase !== 'content') return;
    let i = 0;
    const tick = () => {
      setVisibleMessages(prev => [...prev, MESSAGES[i]]);
      i++;
      if (i < MESSAGES.length) {
        setTimeout(tick, i === MESSAGES.length - 1 ? 600 : 320);
      } else {
        setTimeout(() => setShowButton(true), 500);
      }
    };
    const start = setTimeout(tick, 200);
    return () => clearTimeout(start);
  }, [phase]);

  const handleDismiss = () => {
    setPhase('fadeout');
    setTimeout(onComplete, 600);
  };

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center"
      style={{
        zIndex: 300000,
        background: phase === 'flash' ? '#ffffff' : '#f0f8f0',
        transition: phase === 'fadeout' ? 'opacity 0.6s ease' : undefined,
        opacity: phase === 'fadeout' ? 0 : 1,
        fontFamily: '"Courier New", monospace',
      }}
    >
      {phase !== 'flash' && (
        <>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🌈</div>
            <div style={{
              fontSize: '1.4rem',
              fontWeight: 'bold',
              color: '#2a7a2a',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
            }}>
              Everything Is Perfect
            </div>
            <div style={{ fontSize: '0.7rem', color: '#6aaa6a', marginTop: '0.3rem', letterSpacing: '0.1em' }}>
              A TOTALLY NORMAL REALITY CHECK™
            </div>
          </div>

          {/* Message log */}
          <div style={{
            width: 'min(90vw, 520px)',
            background: '#ffffff',
            border: '2px solid #a8e0a8',
            padding: '1.2rem 1.4rem',
            borderRadius: '2px',
            maxHeight: '50vh',
            overflowY: 'auto',
          }}>
            {visibleMessages.map((msg, i) => (
              <div
                key={i}
                style={{
                  fontSize: '0.72rem',
                  color: i === visibleMessages.length - 1 ? '#1a6a1a' : '#4a8a4a',
                  fontWeight: i === visibleMessages.length - 1 ? 'bold' : 'normal',
                  marginBottom: '0.45rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                  letterSpacing: '0.05em',
                }}
              >
                <span style={{ color: '#6ad06a', flexShrink: 0 }}>{'>'}</span>
                <span>{msg}</span>
              </div>
            ))}
            {visibleMessages.length > 0 && visibleMessages.length < MESSAGES.length && (
              <div style={{ fontSize: '0.72rem', color: '#a0c0a0' }}>
                {'> '}<span style={{ animation: 'blink 1s step-end infinite' }}>▌</span>
              </div>
            )}
          </div>

          {/* Dismiss button */}
          {showButton && (
            <button
              onClick={handleDismiss}
              style={{
                marginTop: '1.8rem',
                padding: '0.6rem 2.2rem',
                background: '#2a7a2a',
                color: '#f0f8f0',
                border: 'none',
                fontSize: '0.75rem',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                fontFamily: '"Courier New", monospace',
              }}
            >
              RETURN TO COMFORT ✓
            </button>
          )}

          <div style={{
            position: 'absolute',
            bottom: '1.2rem',
            fontSize: '0.6rem',
            color: '#a0c0a0',
            letterSpacing: '0.08em',
          }}>
            NORMALCY RESTORATION SYSTEM v2.4 — HAVE A GREAT DAY!
          </div>
        </>
      )}
    </div>
  );
}
